import * as React from "react";
import * as SelectPrimitive from "@radix-ui/react-select";
import { Check, ChevronDown, ChevronUp } from "lucide-react";

import { cn } from "../../lib/utils";

// -------------------------------------------------------------------------
// Shadcn UI primitives for Select
// -------------------------------------------------------------------------

const Select = SelectPrimitive.Root;
const SelectGroup = SelectPrimitive.Group;
const SelectValue = SelectPrimitive.Value;

const SelectTrigger = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Trigger> & {
    variant?: "default" | "glow";
  }
>(({ className, children, variant = "default", ...props }, ref) => (
  <SelectPrimitive.Trigger
    ref={ref}
    className={cn(
      "flex h-12 w-full items-center justify-between rounded-[16px] px-4 py-3 text-sm focus:outline-none disabled:cursor-not-allowed disabled:opacity-50 transition-all duration-200 ease-out",
      variant === "glow"
        ? "border-[1.2px] border-[#7f5cff]/70 bg-[#080611] text-[#E9E9F5] shadow-[0_0_15px_rgba(127,92,246,0.15)_inset] hover:shadow-[0_0_20px_rgba(127,92,246,0.25)_inset]"
        : "bg-white dark:bg-[#080611] border-[1.2px] dark:border-white/10 text-[#E9E9F5]",
      className,
    )}
    {...props}
  >
    {children}
    <SelectPrimitive.Icon asChild>
      <ChevronDown className="h-4 w-4 text-gray-400" />
    </SelectPrimitive.Icon>
  </SelectPrimitive.Trigger>
));
SelectTrigger.displayName = SelectPrimitive.Trigger.displayName;

const SelectScrollUpButton = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.ScrollUpButton>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.ScrollUpButton>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.ScrollUpButton
    ref={ref}
    className={cn(
      "flex cursor-default items-center justify-center py-1",
      className,
    )}
    {...props}
  >
    <ChevronUp className="h-4 w-4" />
  </SelectPrimitive.ScrollUpButton>
));
SelectScrollUpButton.displayName = SelectPrimitive.ScrollUpButton.displayName;

const SelectScrollDownButton = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.ScrollDownButton>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.ScrollDownButton>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.ScrollDownButton
    ref={ref}
    className={cn(
      "flex cursor-default items-center justify-center py-1",
      className,
    )}
    {...props}
  >
    <ChevronDown className="h-4 w-4" />
  </SelectPrimitive.ScrollDownButton>
));
SelectScrollDownButton.displayName =
  SelectPrimitive.ScrollDownButton.displayName;

const SelectContent = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Content> & {
    variant?: "default" | "glow";
  }
>(
  (
    { className, children, position = "popper", variant = "default", ...props },
    ref,
  ) => (
    <SelectPrimitive.Portal>
      <SelectPrimitive.Content
        ref={ref}
        className={cn(
          "relative z-[100] max-h-96 min-w-[8rem] overflow-hidden rounded-2xl border text-[#E9E9F5] shadow-xl data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2",
          variant === "glow"
            ? "bg-white dark:bg-[#0B0B0C] border-[#7f5cff]/50 shadow-purple-500/10"
            : "bg-white dark:bg-[#0B0B0C] border-white/10",
          position === "popper" &&
            "data-[side=bottom]:translate-y-1 data-[side=left]:-translate-x-1 data-[side=right]:translate-x-1 data-[side=top]:-translate-y-1",
          className,
        )}
        position={position}
        {...props}
      >
        <SelectScrollUpButton />
        <SelectPrimitive.Viewport
          className={cn(
            "p-0",
            position === "popper" &&
              "h-[var(--radix-select-trigger-height)] w-full min-w-[var(--radix-select-trigger-width)] max-h-[var(--radix-select-content-available-height)]",
          )}
        >
          {children}
        </SelectPrimitive.Viewport>
        <SelectScrollDownButton />
      </SelectPrimitive.Content>
    </SelectPrimitive.Portal>
  ),
);
SelectContent.displayName = SelectPrimitive.Content.displayName;

const SelectItem = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Item>
>(({ className, children, ...props }, ref) => (
  <SelectPrimitive.Item
    ref={ref}
    className={cn(
      "relative flex w-full cursor-pointer select-none items-center justify-between px-4 py-3 text-sm outline-none transition-colors duration-150 data-[disabled]:pointer-events-none data-[disabled]:opacity-50 text-gray-500 dark:text-[#E9E9F5] hover:bg-white/5 focus:bg-white/5 focus:text-violet-300 data-[state=checked]:text-violet-300 data-[state=checked]:bg-violet-500/10 tracking-wide",
      className,
    )}
    {...props}
  >
    <SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
    <span className="flex h-3.5 w-3.5 items-center justify-center">
      <SelectPrimitive.ItemIndicator>
        <span className="text-xs text-violet-400">✓</span>
      </SelectPrimitive.ItemIndicator>
    </span>
  </SelectPrimitive.Item>
));
SelectItem.displayName = SelectPrimitive.Item.displayName;

// -------------------------------------------------------------------------
// Original StyledSelect Wrapper API
// -------------------------------------------------------------------------

export interface StyledSelectOption {
  value: string;
  label: string;
}

export interface StyledSelectProps {
  value: string;
  onChange: (value: string) => void;
  options: readonly StyledSelectOption[] | StyledSelectOption[];
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  variant?: "default" | "glow";
  icon?: React.ReactNode;
}

export function StyledSelect({
  value,
  onChange,
  options,
  placeholder = "Select an option",
  required = false,
  disabled = false,
  variant = "default",
  icon,
}: StyledSelectProps) {
  // Radix UI Select does not allow empty string ("") as a value for SelectItem.
  // We map it to "__empty__" internally so users can still use "" in their options.
  const internalValue = value === "" ? "__empty__" : value;

  const handleValueChange = (val: string) => {
    onChange(val === "__empty__" ? "" : val);
  };

  return (
    <Select
      value={internalValue}
      onValueChange={handleValueChange}
      disabled={disabled}
      required={required}
    >
      <SelectTrigger variant={variant}>
        <div className="flex items-center gap-3 w-full">
          {icon && <span className="text-gray-500">{icon}</span>}
          <span className={value ? "text-gray-900 dark:text-white" : "text-gray-500"}>
            <SelectValue placeholder={placeholder} />
          </span>
        </div>
      </SelectTrigger>
      <SelectContent variant={variant}>
        {options.map((option: StyledSelectOption) => {
          const optValue = option.value === "" ? "__empty__" : option.value;
          return (
            <SelectItem key={optValue} value={optValue}>
              {option.label}
            </SelectItem>
          );
        })}
      </SelectContent>
    </Select>
  );
}

export default StyledSelect;
